import {Component, inject, OnInit} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, switchMap, take} from 'rxjs/operators';
import {
  DocumentForm,
  EntityClassType,
  EntityType,
  FileForm,
  FileHandlingType,
  FileState,
  InternalNotificationKey,
  ObjectClass,
  SettlementType
} from '|api/commons';
import {FileAuthorizationResult, FileAuthorizedOperation} from '|api/core';
import {
  ApiCrossReferenceService,
  ApiDocumentService,
  ApiFileService,
  ApiSettlementService,
  DocumentDto,
  FileDto,
  FileUpdateDto,
  SettlementDto
} from '|api/document';
import {
  ApplicationConfigService,
  CodebookService,
  CustomFieldsFormComponent,
  CustomFieldsFormUseCase,
  DisposalScheduleRetentionTriggerWidgetComponent,
  DisposalTipDetailLevel,
  EmpowermentComponent,
  EntityClassSelectorComponent,
  EntityClassSelectorUsage,
  EntityItemComponent,
  FILE_MARK_UNAVAILABLE,
  FileDetailCountType,
  FileDetailService,
  FileDtoWithAuthorization,
  FileTypeSelectorComponent,
  FindFunctionalPositionByIdPipe,
  formatDisposalOperation,
  isAuthorizedOperationGranted,
  isFileObject,
  KeywordsSelectorComponent,
  load,
  ObjectClassIconComponent,
  ObjectDetailPart,
  ObjectDetailSubmitActionType,
  OrganizationalStructureService,
  PortraitComponent,
  prepareCustomFieldValuesForBackend,
  RetentionTriggerWidgetMode,
  SecurityCategorySelectorComponent,
  WITHOUT_REF_NUMBER,
} from '|shared';
import {createFileDetailFormGroup, setFileFormDisabledState} from './file-form-definition';
import {FileDetailComponent} from '../../file-detail.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  DocumentFileDetailPreviewComponent
} from '../../../document-shared/components/abstract-object-detail-preview.component';
import {
  AlertComponent,
  AutoFocusDirective,
  CopyToClipboardButtonComponent,
  LabelComponent,
  LinkWithoutHrefDirective,
  LoadingIndicatorService,
  SectionComponent
} from '@icz/angular-essentials';
import {
  DatePickerComponent,
  FormAutocompleteComponent,
  FormFieldComponent,
  IczFormArray,
  IczFormContainerDirective,
  IczFormGroup,
  IczOption
} from '@icz/angular-form-elements';
import {TranslateParser, TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ReactiveFormsModule} from '@angular/forms';
import {
  ObjectAssociationAlertComponent
} from '../../../document-shared/components/object-association-alert/object-association-alert.component';
import {
  ObjectDetailPreviewTransferInfoSectionComponent
} from '../../../document-shared/components/object-detail-preview-transfer-info-section/object-detail-preview-transfer-info-section.component';
import {AsyncPipe} from '@angular/common';
import {
  CaseFileAssociationAlertComponent
} from '../../../document-shared/components/case-file-association-alert/case-file-association-alert.component';
import {
  StorageUnitAssociationAlertComponent
} from '../../../document-shared/components/storage-unit-association-alert/storage-unit-association-alert.component';
import {
  FileCabinetAssociationAlertComponent
} from '../../../document-shared/components/file-cabinet-association-alert/file-cabinet-association-alert.component';
import {
  castStream,
  CheckUnsavedFormPageService,
  enumToOptions,
  namedDtosToOptions
} from '@icz/angular-application-core';
import {
  ApplicationRoute,
  createAbsoluteRoute,
  DocumentsRoute,
  EsslHistoryService,
  FileToastService,
  FileToastType
} from '|ui-essl-core';


@Component({
  selector: 'icz-file-detail-preview',
  templateUrl: './file-detail-preview.component.html',
  styleUrls: ['./file-detail-preview.component.scss'],
  providers: [CheckUnsavedFormPageService],
  imports: [ReactiveFormsModule, AutoFocusDirective, IczFormContainerDirective, ObjectAssociationAlertComponent, LinkWithoutHrefDirective, SectionComponent, CopyToClipboardButtonComponent, FormFieldComponent, FormAutocompleteComponent, DatePickerComponent, LabelComponent, EntityItemComponent, PortraitComponent, AlertComponent, ObjectClassIconComponent, FileTypeSelectorComponent, EntityClassSelectorComponent, DisposalScheduleRetentionTriggerWidgetComponent, EmpowermentComponent, SecurityCategorySelectorComponent, KeywordsSelectorComponent, ObjectDetailPreviewTransferInfoSectionComponent, AsyncPipe, TranslatePipe, CaseFileAssociationAlertComponent, StorageUnitAssociationAlertComponent, FileCabinetAssociationAlertComponent, FindFunctionalPositionByIdPipe, CustomFieldsFormComponent]
})
export class FileDetailPreviewComponent extends DocumentFileDetailPreviewComponent<FileDtoWithAuthorization> implements OnInit {
  protected loadingService = inject(LoadingIndicatorService);
  protected fileDetailService = inject(FileDetailService);
  protected applicationConfigService = inject(ApplicationConfigService);
  private fileToastService = inject(FileToastService);
  private apiFileService = inject(ApiFileService);
  private codebookService = inject(CodebookService);
  private historyService = inject(EsslHistoryService);
  private organizationalStructureService = inject(OrganizationalStructureService);
  private apiDocumentService = inject(ApiDocumentService);
  private apiSettlementService = inject(ApiSettlementService);
  private parentFileDetailComponent = inject(FileDetailComponent, {optional: true});
  private translateService = inject(TranslateService);
  private translateParser = inject(TranslateParser);
  private apiCrossReferenceService = inject(ApiCrossReferenceService);

  file$ = this.fileDetailService.object$;

  form!: IczFormGroup;

  functionalPositionOptions$ = this.organizationalStructureService.functionalPositionsOptions();
  strongCrossReferenceCount = 0;
  isLeadingFile = false;
  strongCrossReferenceFiles: FileDto[] = [];
  leadingFile: Nullable<FileDto>;

  fileFormOptions = enumToOptions('documentForm', DocumentForm);

  fileHandlingTypeOptions = enumToOptions('fileHandlingType', FileHandlingType);

  fileStateOptions = enumToOptions('fileState', FileState);

  settlementTypeOptions: IczOption[] = enumToOptions('settlementType', SettlementType);

  issdApplicationOptions$: Observable<IczOption[]> = this.codebookService.issdApplications().pipe(
    namedDtosToOptions
  );

  settlement: Nullable<SettlementDto>;

  settlementDocument: Nullable<DocumentDto>;

  retentionTriggerValidationMode = new BehaviorSubject<RetentionTriggerWidgetMode>(RetentionTriggerWidgetMode.ENABLED);

  customFieldsValidityReferenceDate: Nullable<Date>;

  get fileState(): FileState {
    return this.fileDetailService.object.fileState;
  }

  get isFileSuspended() {
    return this.fileState === FileState.SUSPENDED;
  }

  get fileTypeId() {
    return this.form.get('fileTypeId')!.value;
  }

  get isFileSettledOrClosed() {
    return this.fileState === FileState.SETTLED || this.fileState === FileState.CLOSED;
  }

  get isFileClosed() {
    return this.fileState === FileState.CLOSED;
  }

  get fileForm(): Nullable<FileForm> {
    return (this.fileDetailService.object as FileDto).fileForm;
  }

  get isFileOpenOrSuspended(): boolean {
    return this.fileState === FileState.OPEN || this.fileState === FileState.SUSPENDED;
  }

  get isFileDeactivated(): boolean {
    return this.fileState === FileState.DEACTIVATED;
  }

  get isFileExported(): boolean {
    return this.fileState === FileState.EXPORTED;
  }

  get isFileDeleted(): boolean {
    return this.fileState === FileState.DELETED;
  }

  get isFileLost(): boolean {
    return (this.fileDetailService.object as FileDto).isLost ?? false;
  }

  get isInExternalApplication() {
    return !isNil(this.form.get('issdId')!.value);
  }

  get settlementType() {
    return this.settlement!.type;
  }

  get entityClassId() {
    return this.form.get('entityClassId')!.value;
  }

  get caseFileId() {
    return this.form.get('caseFileId')!.value;
  }

  get hasStrongCrossReference() {
    return this.strongCrossReferenceCount > 0;
  }
  get leadingFileInfoText() {
    if (this.hasStrongCrossReference) {
      if (this.isLeadingFile) {
        return this.translateParser.interpolate(
          this.translateService.instant(
            'Spis je propojen pevnou vazbou s dalšími spisy ({{count}}). Věcná skupina tohoto spisu řídí ostatní propojené spisy.'
          ),
          {
            count: this.strongCrossReferenceCount,
          })!;
      } else {
        return this.translateParser.interpolate(
          this.translateService.instant(
            'Spis je propojen pevnou vazbou s dalšími spisy ({{count}}). Věcná skupina tohoto spisu se řídí podle řídícího spisu.'
          ),
          {
            count: this.strongCrossReferenceCount,
          })!;
      }
    } else {
      return '';
    }
  }

  get isModifyFileProfilePermissionGranted() {
    return isAuthorizedOperationGranted(this.authorization, FileAuthorizedOperation.FILE_MODIFY_PROFILE);
  }
  get strongCrossReferenceHeader() {
    if (this.hasStrongCrossReference) {
      return this.translateParser.interpolate(
        this.translateService.instant(
          'Spis je propojen s dalšími spisy ({{count}}) pevnou vazbou'
        ),
        {
          count: this.strongCrossReferenceCount,
        })!;
    } else {
      return '';
    }
  }

  get auditInfo() {
    return (this.fileDetailService.object as FileDtoWithAuthorization).auditInfo!;
  }

  get noteFieldGrowClass() {
    if (this.isFileSuspended) {
      return 'grow-3';
    }
    else if (this.isFileSettledOrClosed) {
      return 'grow-2';
    }
    else {
      return 'grow-4';
    }
  }

  readonly FileForm = FileForm;
  readonly FileState = FileState;
  readonly SettlementType = SettlementType;
  readonly FileDetailCountType = FileDetailCountType;
  readonly DisposalTipDetailLevel = DisposalTipDetailLevel;
  readonly isFileObject = isFileObject;

  override ngOnInit(): void {
    super.ngOnInit();

    this.fileDetailService.object$.pipe(
      filter(Boolean),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(file => {
      if (file) {
        this.authorization = file.authorization as FileAuthorizationResult;

        this.form = createFileDetailFormGroup();
        this.getStorageUnit(file);
        this.getCaseFile(file);
        this.getFileCabinet(file.fileCabinetId);

        this.apiCrossReferenceService.crossReferenceGetStrongCrossReference({
          fileId: file.id
        }).subscribe(strongCrossReferenceDto => {
          if (strongCrossReferenceDto && strongCrossReferenceDto.files.length > 0) {
            this.strongCrossReferenceFiles = strongCrossReferenceDto.files.filter(f => f.id !== file.id);
            this.strongCrossReferenceCount = strongCrossReferenceDto.files.length - 1;
            this.leadingFile = strongCrossReferenceDto.files.find(f => f.id === strongCrossReferenceDto.leadingFileId);
            this.isLeadingFile = this.leadingFile!.id === file.id;
          }

          this.initializePreviewForm(file);
        });
      }
    });
  }

  openFileDetail(file: FileDto) {
    this.router.navigateByUrl(createAbsoluteRoute(ApplicationRoute.DOCUMENTS, DocumentsRoute.FILE, file.id));
  }

  onSubmit(submitActionType: ObjectDetailSubmitActionType) {
    this.file$.pipe(
      castStream<FileDto>(),
      take(1),
    ).subscribe(file => {
      this.checkCrossReferences([], [file.id]).subscribe((crossUpdate: boolean) => {
        let update$: Nullable<Observable<FileDto>>;

        const formValue = this.form.getRawValue();

        const updateDto: FileUpdateDto = {
          closureDate: file.closureDate!,
          deactivationReason: file.deactivationReason!,
          description: formValue.description,
          empowerment: formValue.empowerment,
          entityClassId: formValue.entityClassId,
          executionDate: file.executionDate!,
          fileHandlingType: file.fileHandlingType,
          fileReferenceCreationType: file.fileReferenceCreationType,
          fileState: file.fileState,
          fileTypeId: formValue.fileTypeId,
          holderFunctionalPositionIds: file.holderFunctionalPositionIds,
          initialDocumentId: file.initialDocumentId,
          keywordIds: formValue.keywordIds,
          label: formValue.label,
          lockedState: null,
          note: formValue.note,
          objectClass: file.objectClass,
          ownerFunctionalPositionId: file.ownerFunctionalPositionId!,
          pageCount: file.pageCount!,
          physicalLocation: formValue.physicalLocation,
          relatedEvidence: formValue.relatedEvidence,
          resolutionDate: formValue.resolutionDate,
          securityCategoryId: formValue.securityCategoryId,
          subject: formValue.subject,
          fileSuspensions: formValue.fileSuspensions,
          yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
          triggerEventCheckYear: formValue.triggerEventCheckYear,
          updateCrossReferencedObjects: crossUpdate,
          customFieldValues: prepareCustomFieldValuesForBackend(formValue.customFieldValues),
        };

        if (isAuthorizedOperationGranted(this.authorization, FileAuthorizedOperation.FILE_MODIFY_PROFILE)) {
          update$ = this.fileDetailService.objectId$.pipe(
            take(1),
            switchMap(id =>
              this.apiFileService.fileUpdate({
                id,
                body: updateDto as FileUpdateDto,
              })
            )
          );
        }

        if (update$) {
          this.loadingService
            .doLoading(update$, this.parentFileDetailComponent!)
            .subscribe(result => {
              this.fileToastService.dispatchFileInfoToast(FileToastType.FILE_UPDATED, {
                [InternalNotificationKey.FILE_ID]: result.id,
                [InternalNotificationKey.FILE_SUBJECT]: result.subject,
              });
              this.form.markAsPristine();
              this.fileDetailService.submitFinished$.next();

              if (submitActionType === ObjectDetailSubmitActionType.SAVE_AND_CLOSE) {
                this.historyService.routeBack(createAbsoluteRoute(ApplicationRoute.DOCUMENTS));
              }
              else {
                this.fileDetailService.alreadySaved = true;
                this.fileDetailService.reloadObject({
                  [ObjectDetailPart.OBJECT_DATA]: load(),
                });

                if (!isNil(this.hideSubmitBarTimeout)) {
                  clearTimeout(this.hideSubmitBarTimeout);
                }

                this.hideSubmitBarTimeout = setTimeout(() => {
                  this.fileDetailService.alreadySaved = false;
                }, 5000) as unknown as number;
              }
            });
        }
      });
    });
  }

  protected canShowFileProfile(file: FileDtoWithAuthorization) {
    return isAuthorizedOperationGranted(file.authorization, FileAuthorizedOperation.FILE_SHOW_PROFILE);
  }

  protected getRefNumberValue(file: FileDtoWithAuthorization) {
    if (this.canShowFileProfile(file) || this.applicationConfigService.objectExistenceCheckObjectRefNumberVisible()) {
      return file.refNumber ?? this.translateService.instant(WITHOUT_REF_NUMBER);
    }
    else {
      return this.translateService.instant(FILE_MARK_UNAVAILABLE);
    }
  }

  private initializePreviewForm(file: FileDtoWithAuthorization) {
    this.fileDetailService.form$.next(this.form);

    this.updateFormWithCurrentFileData(file);
    this.fileDetailService.showSubmitBar = true;
  }

  private updateFormWithCurrentFileData(file: FileDtoWithAuthorization) {
    if (file) {
      if (this.canViewFullContent(file)) {
        const customFieldsForm = this.form.get('customFieldValues') as IczFormArray;
        customFieldsForm.enable();
        customFieldsForm.setSize(file.customFieldValues?.length ?? 0);
      }

      const currentSuspension = (file.fileSuspensions ?? []).find(s => !s.resumedAt);

      this.form.patchValue({
        ...file,
        resumptionDeadlineAt: currentSuspension?.resumptionDeadlineAt,
      });
      this.initCustomFieldsValidityReferenceDate();

      /* This whole section of code is dealing with enable/disable state of form. It must be enclosed in setTimeout
      *  otherwise runtime switching from disabled -> enabled and vice versa won't work properly.
      *  problem is that when file is reloaded the reference to this.form is changed and messes up rerendering of form state.
      *  */
      setTimeout(() => {
        this.form.enable();
        setFileFormDisabledState(this.form);

        if (!this.isModifyFileProfilePermissionGranted || this.isFileDeleted || this.isFileDeactivated || this.isFileExported || this.fileDetailService.lockedState) {
          this.form.disable();
          this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.DISABLED);
        }
        else if (this.isFileOpenOrSuspended) {
          this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.ENABLED_START_YEAR);
          this.form.get('resumptionDeadlineAt')?.disable();
          this.form.get('empowerment')?.enable();
          this.form.get('label')?.enable();

          if (file.caseFile || (this.hasStrongCrossReference && !this.isLeadingFile)) {
            this.form.get('entityClassId')!.disable();
            this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.DISABLED);
          }
        }
        else if (this.isFileSettledOrClosed) {
          this.form.disable();

          this.form.get('subject')!.enable();
          this.form.get('fileTypeId')!.enable();
          this.form.get('description')!.enable();
          this.form.get('note')!.enable();
          this.form.get('securityCategoryId')!.enable();
          this.form.get('keywordIds')!.enable();
          this.form.get('yearOfRetentionPeriodStart')!.enable();
          this.form.get('disposalOperationCode')?.setValue(formatDisposalOperation(file.disposalOperationCode, this.translateService));
          if (this.isFileClosed) {
            this.form.get('triggerEventCheckYear')!.enable();
            this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.VALIDATED);
          }
        }

        if (file.settlementId) {
          this.apiSettlementService.settlementFindById({
            id: file.settlementId!
          }).subscribe(settlement => {
            this.settlement = settlement;

            this.form.get('customFieldValues')!.disable();
            this.initCustomFieldsValidityReferenceDate();

            if (settlement.type === SettlementType.BY_DOCUMENT) {
              this.apiDocumentService.documentFindById({id: settlement.relatedDocumentId!}).subscribe( doc => {
                this.settlementDocument = doc;
              });
            }
            if (settlement.type === SettlementType.BY_OTHER_METHOD) {
              this.fillOtherSettlementMethod(this.codebookService, this.translateService, settlement.settlementMethodId);
            }
          });
        }
      });
    }
  }

  private canViewFullContent(file: FileDtoWithAuthorization) {
    return this.canShowFileProfile(file) && file.fileState !== FileState.DELETED;
  }

  private initCustomFieldsValidityReferenceDate() {
    if (this.isFileSettledOrClosed) {
      const settlementDate = this.settlement?.settlementDate;
      this.customFieldsValidityReferenceDate = settlementDate ? new Date(settlementDate) : null;
    }
    else {
      this.customFieldsValidityReferenceDate = new Date();
    }
  }

  protected readonly ObjectClass = ObjectClass;
  protected readonly EntityType = EntityType;
  protected readonly EntityClassType = EntityClassType;
  protected readonly EntityClassSelectorUsage = EntityClassSelectorUsage;
  protected readonly CustomFieldsFormUseCase = CustomFieldsFormUseCase;
}
